<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { vOnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { debounce } from '@chatwoot/utils';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import {
  searchContacts,
  createNewContact,
  fetchContactableInboxes,
  processContactableInboxes,
  mergeInboxDetails,
} from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import wootConstants from 'dashboard/constants/globals';

import ComposeNewConversationForm from 'dashboard/components-next/NewConversation/components/ComposeNewConversationForm.vue';

const props = defineProps({
  alignPosition: {
    type: String,
    default: 'left',
  },
  contactId: {
    type: String,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const { width: windowWidth } = useWindowSize();

const { fetchSignatureFlagFromUISettings } = useUISettings();

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const viewInModal = computed(() => props.isModal || isSmallScreen.value);

const contacts = ref([]);
const selectedContact = ref(null);
const targetInbox = ref(null);
const isCreatingContact = ref(false);
const isFetchingInboxes = ref(false);
const isSearching = ref(false);
const showComposeNewConversation = ref(false);

const contactById = useMapGetter('contacts/getContactById');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const uiFlags = useMapGetter('contactConversations/getUIFlags');
const messageSignature = useMapGetter('getMessageSignature');
const inboxesList = useMapGetter('inboxes/getInboxes');

const sendWithSignature = computed(() =>
  fetchSignatureFlagFromUISettings(targetInbox.value?.channelType)
);

const directUploadsEnabled = computed(
  () => globalConfig.value.directUploadsEnabled
);

const activeContact = computed(() => contactById.value(props.contactId));

const triggerRef = ref(null);
const modalPosition = ref({ top: 0, left: 0 });

const updateModalPosition = () => {
  if (!triggerRef.value || viewInModal.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  const isRTL = document.documentElement.dir === 'rtl';

  // Position du modal en dessous du trigger
  let top = rect.bottom + 8; // 8px offset
  let left = rect.left;

  // Pour alignPosition='right', aligner à gauche du trigger
  if (props.alignPosition === 'right') {
    left = rect.left;
  } else {
    // Pour alignPosition='left', aligner à droite du trigger
    left = rect.right - 672; // 672px = 42rem (largeur du modal)
  }

  // Pour RTL, inverser la logique
  if (isRTL) {
    if (props.alignPosition === 'right') {
      left = rect.right - 672;
    } else {
      left = rect.left;
    }
  }

  modalPosition.value = { top, left };
};

const composePopoverClass = computed(() => {
  if (viewInModal.value) return '';

  return props.alignPosition === 'right'
    ? 'absolute ltr:left-0 ltr:right-[unset] rtl:right-0 rtl:left-[unset]'
    : 'absolute rtl:left-0 rtl:right-[unset] ltr:right-0 ltr:left-[unset]';
});

const onContactSearch = debounce(
  async query => {
    isSearching.value = true;
    contacts.value = [];
    try {
      contacts.value = await searchContacts(query);
      isSearching.value = false;
    } catch (error) {
      useAlert(t('COMPOSE_NEW_CONVERSATION.CONTACT_SEARCH.ERROR_MESSAGE'));
    } finally {
      isSearching.value = false;
    }
  },
  300,
  false
);

const resetContacts = () => {
  contacts.value = [];
};

const handleSelectedContact = async ({ value, action, ...rest }) => {
  let contact;
  if (action === 'create') {
    isCreatingContact.value = true;
    try {
      contact = await createNewContact(value);
      isCreatingContact.value = false;
    } catch (error) {
      isCreatingContact.value = false;
      return;
    }
  } else {
    contact = rest;
  }
  selectedContact.value = contact;
  if (contact?.id) {
    isFetchingInboxes.value = true;
    try {
      const contactableInboxes = await fetchContactableInboxes(contact.id);
      // Merge the processed contactableInboxes with the inboxesList
      selectedContact.value.contactInboxes = mergeInboxDetails(
        contactableInboxes,
        inboxesList.value
      );

      isFetchingInboxes.value = false;
    } catch (error) {
      isFetchingInboxes.value = false;
    }
  }
};

const handleTargetInbox = inbox => {
  targetInbox.value = inbox;
  resetContacts();
};

const clearSelectedContact = () => {
  selectedContact.value = null;
  targetInbox.value = null;
};

const closeCompose = () => {
  showComposeNewConversation.value = false;
  if (!props.contactId) {
    // If contactId is passed as prop
    // Then don't allow to remove the selected contact
    selectedContact.value = null;
  }
  targetInbox.value = null;
  resetContacts();
  emit('close');
};

const createConversation = async ({ payload, isFromWhatsApp }) => {
  try {
    const data = await store.dispatch('contactConversations/create', {
      params: payload,
      isFromWhatsApp,
    });
    const action = {
      type: 'link',
      to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
      message: t('COMPOSE_NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
    };
    closeCompose();
    useAlert(t('COMPOSE_NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'), action);
    return true; // Return success
  } catch (error) {
    useAlert(
      error instanceof ExceptionWithMessage
        ? error.data
        : t('COMPOSE_NEW_CONVERSATION.FORM.ERROR_MESSAGE')
    );
    return false; // Return failure
  }
};

const toggle = async () => {
  showComposeNewConversation.value = !showComposeNewConversation.value;
  if (showComposeNewConversation.value && !viewInModal.value) {
    await nextTick();
    updateModalPosition();
  }
};

watch(
  activeContact,
  (currentContact, previousContact) => {
    if (currentContact && props.contactId) {
      // Reset on contact change
      if (currentContact?.id !== previousContact?.id) clearSelectedContact();

      // First process the contactable inboxes to get the right structure
      const processedInboxes = processContactableInboxes(
        currentContact.contactInboxes || []
      );
      // Then Merge processedInboxes with the inboxes list
      selectedContact.value = {
        ...currentContact,
        contactInboxes: mergeInboxDetails(processedInboxes, inboxesList.value),
      };
    }
  },
  { immediate: true, deep: true }
);

const handleClickOutside = () => {
  if (!showComposeNewConversation.value) return;

  showComposeNewConversation.value = false;
  emit('close');
};

const onModalBackdropClick = () => {
  if (!viewInModal.value) return;
  handleClickOutside();
};

onMounted(() => resetContacts());

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showComposeNewConversation.value) {
        showComposeNewConversation.value = false;
      }
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="relative">
    <div ref="triggerRef">
      <slot
        name="trigger"
        :is-open="showComposeNewConversation"
        :toggle="toggle"
      />
    </div>

    <!-- Modal en mode plein écran -->
    <Teleport v-if="viewInModal" to="body">
      <Transition
        enter-active-class="transition-all duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
        enter-from-class="opacity-0 scale-90"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-200 ease-[cubic-bezier(0.4,0,1,1)]"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showComposeNewConversation"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: ['div.ProseMirror-prompt'] },
          ]"
          class="fixed z-[9999] bg-n-alpha-black1 backdrop-blur-[4px] flex items-start pt-[clamp(3rem,15vh,12rem)] justify-center inset-0"
          @click.self="onModalBackdropClick"
        >
          <ComposeNewConversationForm
            :contacts="contacts"
            :contact-id="contactId"
            :is-loading="isSearching"
            :current-user="currentUser"
            :selected-contact="selectedContact"
            :target-inbox="targetInbox"
            :is-creating-contact="isCreatingContact"
            :is-fetching-inboxes="isFetchingInboxes"
            :is-direct-uploads-enabled="directUploadsEnabled"
            :contact-conversations-ui-flags="uiFlags"
            :contacts-ui-flags="contactsUiFlags"
            :message-signature="messageSignature"
            :send-with-signature="sendWithSignature"
            @search-contacts="onContactSearch"
            @reset-contact-search="resetContacts"
            @update-selected-contact="handleSelectedContact"
            @update-target-inbox="handleTargetInbox"
            @clear-selected-contact="clearSelectedContact"
            @create-conversation="createConversation"
            @discard="closeCompose"
          />
        </div>
      </Transition>
    </Teleport>

    <!-- Popover en mode desktop -->
    <Teleport v-if="!viewInModal" to="body">
      <Transition
        enter-active-class="transition-all duration-[350ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
        enter-from-class="opacity-0 scale-90"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition-all duration-200 ease-[cubic-bezier(0.4,0,1,1)]"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="showComposeNewConversation"
          v-on-click-outside="[
            handleClickOutside,
            { ignore: ['div.ProseMirror-prompt'] },
          ]"
          class="fixed z-[9999]"
          :style="{
            top: `${modalPosition.top}px`,
            left: `${modalPosition.left}px`,
          }"
        >
          <div class="mt-2">
            <ComposeNewConversationForm
              :contacts="contacts"
              :contact-id="contactId"
              :is-loading="isSearching"
              :current-user="currentUser"
              :selected-contact="selectedContact"
              :target-inbox="targetInbox"
              :is-creating-contact="isCreatingContact"
              :is-fetching-inboxes="isFetchingInboxes"
              :is-direct-uploads-enabled="directUploadsEnabled"
              :contact-conversations-ui-flags="uiFlags"
              :contacts-ui-flags="contactsUiFlags"
              :message-signature="messageSignature"
              :send-with-signature="sendWithSignature"
              @search-contacts="onContactSearch"
              @reset-contact-search="resetContacts"
              @update-selected-contact="handleSelectedContact"
              @update-target-inbox="handleTargetInbox"
              @clear-selected-contact="clearSelectedContact"
              @create-conversation="createConversation"
              @discard="closeCompose"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
